/**
 * IdentityVerificationSection — residency type / ID type / ID number / ID
 * expiration + the matching-records save-gate. Mirrors Flutter
 * `identity_verification_form.dart` + `identity_child_widget.dart`.
 *
 * Field order (Flutter `identity_child_widget.dart`, `!isGuardian`):
 *   Residency type (pills) · ID type (pills) · ID number · ID expiration date.
 *
 * The ID-card front/back scan tiles are NOT here — Flutter renders them at the
 * top of the page under the bare "ID CARD" label (`personal_details_widget.dart`),
 * so they live in the tab now.
 *
 * Save-gate behavior (Flutter parity): when ID type + ID number both have
 * values, fire a debounced (500ms) `useMatchingPatients` lookup; if matches
 * > 0 the Save is blocked until the user resolves the duplicate (PROD-726
 * "use anyway"). The count is reported up via `onMatchingCountChange`.
 */

import { useEffect, useMemo, useState } from 'react';
import { useFormContext, useWatch, Controller } from 'react-hook-form';
import { AlertTriangle } from 'lucide-react';
import { Button, BrandSpinner } from '@scripsteam/scrips-signal-ds';
import { TextField } from '@scripsteam/scrips-signal-ds';
import { DatePickerField } from '@/shared/ui/components';
import { useIdTypes } from '@/shared/services/lookups';
import { usePatientResidencyTypes } from '@/patients/services/use-patient-residency-types';
import { useMatchingPatients } from '@/patients/services/use-matching-patients';
import { useDebounce } from '@/shared/hooks/use-debounce';
import { formatEmiratesId } from './emirates-id-format';
import { idExpiryMin } from './id-expiry-bounds';
import { DuplicateMatchDialog } from './duplicate-match-dialog';
import { useSuppressibleCount } from './use-suppressible-count';
import { excludeOwnMatch } from '@/patients/lib/exclude-own-match';
import { isEmiratesIdType } from '@/patients/lib/identity-constants';
import { PillPicker } from './form-primitives';
import { useMissingFields } from './missing-fields-context';
import { FieldLabel } from '@scripsteam/scrips-signal-ds';
import type { PersonalDetailsFormValues } from './personal-details-schema';

interface IdentityVerificationSectionProps {
  /** Reports the current matching-records count back to the tab so the
   *  save action can be gated. 0 means clean. */
  onMatchingCountChange?: (count: number) => void;
  /** Hide the matching-records warning banner — used in the Guardian
   *  reuse where the same form composes but the gate logic differs. */
  hideMatchingWarning?: boolean;
  /** The patient being edited (null in create mode). Used to exclude the
   *  patient's OWN record from the duplicate matches — Flutter
   *  `create_patient_screen.dart:389` removes `idNumber == initialIdNumber`
   *  so an existing patient never flags as its own duplicate. In create mode
   *  this is null → nothing excluded → real duplicates still block Save. */
  currentPatientId?: string | null;
  /** The patient's ID type/number as loaded (edit mode). The duplicate
   *  lookup only fires once the user CHANGES the ID from these values —
   *  Flutter's `isInitial` guard (`create_patient_screen.dart:341-349`) skips
   *  the match on initial load. Null/absent in create mode → any entered ID
   *  is "changed" and fires the check. */
  initialIdType?: string | null;
  initialIdNumber?: string | null;
}

export function IdentityVerificationSection({
  onMatchingCountChange,
  hideMatchingWarning = false,
  currentPatientId = null,
  initialIdType = null,
  initialIdNumber = null,
}: IdentityVerificationSectionProps) {
  const { control, register, setValue, formState } =
    useFormContext<PersonalDetailsFormValues>();
  const { errors } = formState;
  const missing = useMissingFields();

  const idType = useWatch({ control, name: 'identification.idType' });
  const idNumber = useWatch({ control, name: 'identification.idNumber' });
  // Residency type lives in this section in Flutter (`identity_child_widget`
  // renders it first, before ID type), not in Demographics.
  const residency = useWatch({ control, name: 'residencyType' });

  const idTypesQ = useIdTypes();
  const residenciesQ = usePatientResidencyTypes();

  // "Is this an Emirates ID?" keys off the idType GUID, mirroring Flutter
  // (`identity_child_widget.dart:299`). The old name-match
  // (`.includes('emirates')`) was dead — `GET /api/Master/IdentityType` never
  // returns a name containing "emirates" — so the mask + 15-digit cap never
  // applied and raw digits were persisted (PROD-1756). GUID is the truth.
  const isEmiratesId = isEmiratesIdType(idType);

  // Debounce the (idType, idNumber) pair before firing the matching-records
  // lookup — Flutter uses 500 ms (`create_patient_screen.dart:392`).
  const trimmedIdNumber = (idNumber ?? '').trim();
  const debounced = useDebounce(
    { idType: idType ?? '', idNumber: trimmedIdNumber },
    500,
  );
  // Only fire the duplicate lookup once the ID has CHANGED from what was
  // loaded — Flutter skips the match on initial load (`isInitial` guard) and
  // only re-checks on the ID field's onChange. In create mode the initial
  // values are empty, so any entered ID counts as "changed". This is what
  // stops the banner from flashing on page open with the patient's own ID.
  const idChanged =
    debounced.idType !== (initialIdType ?? '') ||
    debounced.idNumber !== (initialIdNumber ?? '').trim();
  const matchingQ = useMatchingPatients(
    debounced.idType && debounced.idNumber && idChanged
      ? { idType: debounced.idType, idNumber: debounced.idNumber }
      : null,
  );
  // Exclude the patient's own record (edit mode) — Flutter removes the
  // initial-id match so a patient never flags as its own duplicate. Memoized
  // so the array reference is stable across unrelated re-renders (the
  // suppression hook resets "use anyway" on a new reference).
  const matches = useMemo(
    () => excludeOwnMatch(matchingQ.data ?? [], currentPatientId),
    [matchingQ.data, currentPatientId],
  );
  const [dialogOpen, setDialogOpen] = useState(false);

  const { effectiveCount, rawCount, suppress } = useSuppressibleCount(matches);

  useEffect(() => {
    onMatchingCountChange?.(effectiveCount);
  }, [effectiveCount, onMatchingCountChange]);

  // Date helpers — input type=date wants YYYY-MM-DD; backend wants
  // `YYYY-MM-DDTHH:mm:ss.sss` (no Z) per the create curl.
  const toInput = (iso: string | null | undefined) => (iso ? iso.split('T')[0] ?? '' : '');
  const fromInput = (yyyymmdd: string) => (yyyymmdd ? `${yyyymmdd}T00:00:00.000` : null);

  return (
    <div className="grid gap-6">
      {/* Residency type (pills) */}
      <div>
        <FieldLabel>Residency type</FieldLabel>
        <div className="mt-2">
          {residenciesQ.isPending ? (
            <BrandSpinner size={20} label="Loading…" />
          ) : !residenciesQ.data || residenciesQ.data.length === 0 ? (
            <span className="text-sm text-text-muted">
              No residency types available
            </span>
          ) : (
            <PillPicker
              options={residenciesQ.data.map((r) => ({
                value: r.code,
                label: r.display,
              }))}
              value={residency?.code ?? null}
              aria-invalid={!!errors.residencyType || missing.has('residencyType')}
              onChange={(v) => {
                const r = residenciesQ.data?.find((x) => x.code === v);
                setValue(
                  'residencyType',
                  r
                    ? { code: r.code, display: r.display, definition: r.definition ?? null }
                    : null,
                  { shouldDirty: true },
                );
              }}
            />
          )}
        </div>
      </div>

      {/* ID type (pills) */}
      <div data-missing-field={missing.has('idType') || undefined}>
        <FieldLabel>ID type</FieldLabel>
        <div className="mt-2">
          {idTypesQ.isPending ? (
            <BrandSpinner size={20} label="Loading…" />
          ) : (
            <Controller
              control={control}
              name="identification.idType"
              render={({ field }) => (
                <PillPicker
                  options={(idTypesQ.data ?? []).map((t) => ({
                    value: t.id,
                    label: t.name,
                  }))}
                  value={field.value ?? null}
                  onChange={(v) => field.onChange(v || null)}
                  aria-invalid={!!errors.identification?.idType || missing.has('idType')}
                />
              )}
            />
          )}
        </div>
      </div>

      {/* ID number */}
      <div>
        <FieldLabel>ID number</FieldLabel>
        {isEmiratesId ? (
          // Emirates ID — mask the input as the user types. Strip the
          // mask on store so the wire body ships unformatted digits.
          <Controller
            control={control}
            name="identification.idNumber"
            render={({ field }) => (
              <TextField
                value={field.value ? formatEmiratesId(String(field.value)) : ''}
                onChange={(e) => {
                  const digits = e.target.value.replace(/\D+/g, '').slice(0, 15);
                  field.onChange(digits || null);
                }}
                placeholder="784-YYYY-XXXXXXX-X"
                className="mt-1" size="compact"
                inputMode="numeric"
                aria-invalid={!!errors.identification?.idNumber}
                status={missing.has('idNumber') ? 'error' : undefined}
              />
            )}
          />
        ) : (
          <TextField
            {...register('identification.idNumber', {
              setValueAs: (v) => (v === '' ? null : v),
            })}
            placeholder="Enter ID number"
            className="mt-1" size="compact"
            aria-invalid={!!errors.identification?.idNumber}
            status={missing.has('idNumber') ? 'error' : undefined}
          />
        )}
      </div>

      {/* ID expiration date */}
      <div>
        <FieldLabel>ID expiration date</FieldLabel>
        <Controller
          control={control}
          name="identification.idExpirationDate"
          render={({ field }) => (
            <DatePickerField
              value={toInput(field.value)}
              onChange={(v) => field.onChange(fromInput(v))}
              // PROD-1762: Flutter constrains this with `firstDate:
              // DateTime.now()` (identity_child_widget.dart:381). The bound was
              // never ported, so every past date was selectable — an ID could be
              // recorded as expiring last year. Today stays selectable; see
              // id-expiry-bounds.ts for why that is the correct inclusive edge.
              min={idExpiryMin()}
              className="mt-1"
            />
          )}
        />
      </div>

      {/* Matching-records warning + dialog opener (PROD-726). */}
      {!hideMatchingWarning && effectiveCount > 0 ? (
        <div className="flex items-start gap-3 rounded-md border border-status-warning bg-status-warning-bg px-3 py-2">
          <AlertTriangle className="size-4 flex-shrink-0 mt-1 text-status-amber-text" />
          <div className="flex-1">
            <p className="text-sm font-semibold text-status-amber-text">
              {rawCount === 1
                ? '1 potential duplicate found'
                : `${rawCount} potential duplicates found`}
            </p>
            <p className="mt-1 text-xs text-status-amber-text">
              Save is blocked until you review the matches or choose to
              continue with a new record.
            </p>
          </div>
          <Button
            type="button"
            size="sm"
            variant="secondary"
            onClick={() => setDialogOpen(true)}
            className="flex-shrink-0"
          >
            View {rawCount}
          </Button>
        </div>
      ) : null}

      <DuplicateMatchDialog
        open={dialogOpen}
        onOpenChange={setDialogOpen}
        matches={matches}
        source="patient-identity"
        onUseAnyway={suppress}
      />
    </div>
  );
}
